import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { havePermission } from "../../roles/Roles";
import { IsAllow } from "../../utils/allowHelper/AllowHelper";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const CampaignsMenu = ({ children }) => {
  const { translate } = useTranslator();
  const { user } = useSelector((state) => state.user);
  const [companyAllow, setCompanyAllow] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const [menuOptions] = useState([
    {
      key: "campaigns",
      name: translate("campaigns"),
      link: "/campaigns",
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
          />
        </svg>
      ),
      permission: ["all"],
    },
    {
      key: "new-campaign",
      name: translate("new-campaign"),
      link: "/campaigns/new",
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4v16m8-8H4"
          />
        </svg>
      ),
      permission: ["all"],
    },
    {
      key: "customers-list",
      name: translate("customers-list"),
      link: "/campaigns/lists",
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 110-8 4 4 0 010 8zm6 4a2 2 0 100-4 2 2 0 000 4zM3 18a2 2 0 100-4 2 2 0 000 4z"
          />
        </svg>
      ),
      permission: ["all"],
    },
  ]);

  useEffect(() => {
    if (company?.allow) setCompanyAllow({ ...company.allow });
  }, [company]);

  // Detect active link
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";
  const isActive = (link) => currentPath === link;

  if (userLoading || companyLoading) return null;

  const NavItem = ({ option }) => {
    const active = isActive(option.link);
    const allowed = havePermission(user, option.permission);
    const sbAllowed =
      option.key !== "cold-accounts" && option.key !== "deposit-types"
        ? true
        : IsAllow(companyAllow, "SBFields");

    if (!allowed || !sbAllowed) return null;

    return (
      <li>
        <a
          href={option.link}
          className={`
            group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
            transition-all duration-150 select-none
            ${
              active
                ? "bg-blue-700 text-white shadow-sm shadow-blue-200 dark:bg-blue-600 dark:shadow-blue-900"
                : "text-gray-600 hover:bg-blue-50 hover:text-blue-700 dark:text-gray-300 dark:hover:bg-blue-900/40 dark:hover:text-blue-300"
            }
          `}
        >
          {/* Active indicator bar */}
          {active && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-white/40" />
          )}

          {/* Icon container */}
          <span
            className={`
            flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors duration-150
            ${
              active
                ? "bg-white/20 text-white"
                : "bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-700 dark:bg-gray-700 dark:text-gray-400 dark:group-hover:bg-blue-900/60 dark:group-hover:text-blue-300"
            }
          `}
          >
            {option.icon}
          </span>

          <span className="truncate">{option.name}</span>
        </a>
      </li>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Section header */}
      <div className="px-3 pt-4 pb-3 mb-1">
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded flex items-center justify-center"
            style={{ background: "rgb(26 86 219)" }}
          >
            <svg
              width="11"
              height="11"
              fill="none"
              viewBox="0 0 24 24"
              stroke="white"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
              />
            </svg>
          </div>
          <span className="text-xs font-bold uppercase tracking-widest text-blue-700 dark:text-blue-400">
            Campaigns
          </span>
        </div>
        {/* Thin blue divider */}
        <div className="mt-3 h-px bg-gradient-to-r from-blue-200 via-blue-100 to-transparent dark:from-blue-800 dark:via-blue-900/50" />
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2">
        <ul className="space-y-1">
          {menuOptions.map((option) => (
            <NavItem key={option.key} option={option} />
          ))}
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      {/* ── Mobile toggle ── */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed bottom-5 right-5 z-50 sm:hidden flex items-center justify-center w-12 h-12 rounded-2xl text-white shadow-lg shadow-blue-300 dark:shadow-blue-900 transition-transform active:scale-95"
        style={{ background: "rgb(26 86 219)" }}
        aria-label="Open campaigns menu"
      >
        <svg
          width="20"
          height="20"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 6h16M4 12h16M4 18h7"
          />
        </svg>
      </button>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 sm:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-sm dark:bg-gray-900/60" />
          <div
            className="absolute left-0 top-0 h-full w-56 bg-white shadow-2xl dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/60">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-700 dark:text-blue-400">
                Menu
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* ── Layout ── */}
      <div className="flex min-h-screen">
        {/* Secondary sidebar — desktop */}
        <aside className="hidden sm:flex flex-col w-52 flex-shrink-0 border-r border-gray-100 bg-white dark:border-gray-700/60 dark:bg-gray-900">
          <SidebarContent />
        </aside>

        {/* Page content */}
        <main className="flex-1 min-w-0 p-5 md:p-7 bg-gray-50 dark:bg-gray-950">
          {children}
        </main>
      </div>
    </>
  );
};

export default CampaignsMenu;
